import {
  getTypeDecimals,
  resolveDecimals,
  type EnsoDecimals,
  type FormItem,
  type TableColumnFormat,
  type TableColumns,
} from '@ohpen/enso-ui';

// Decimals every numeric field keeps unless its column or form item sets its own.
export const PMP_DECIMALS: EnsoDecimals = { currency: 2, percentage: 2 };

// Base rates and the rates built on them are published with four decimals.
export const RATE_DECIMALS = 4;

const resolvedDecimals = resolveDecimals(PMP_DECIMALS);

/** The decimals a column keeps: its own decimalScale, else the default for its format. Undefined keeps every decimal. */
export const getColumnDecimals = (format?: TableColumnFormat | null): number | undefined =>
  format?.decimalScale ?? getTypeDecimals(resolvedDecimals, format?.type) ?? undefined;

const NUMERIC_VALUE_TYPES = new Set(['digit', 'money', 'percent']);

/** The decimals each field keeps in these columns, by dataIndex. The first column on a field wins. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getDecimalsByField = (...columnSets: TableColumns<any>[]): Readonly<Record<string, number>> =>
  columnSets.flat().reduce<Record<string, number>>((byField, column) => {
    const decimals = getColumnDecimals(column.options?.format);
    if (typeof column.dataIndex === 'string' && decimals !== undefined && !(column.dataIndex in byField)) {
      byField[column.dataIndex] = decimals;
    }
    return byField;
  }, {});

/** The columns with each numeric column's decimals taken from the source column on the same field, unless it sets its own. */
export const withSourceDecimals = <T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  source: TableColumns<any>[],
  columns: TableColumns<T>,
): TableColumns<T> => {
  const byField = getDecimalsByField(...source);
  return columns.map(column => {
    const format = column.options?.format;
    const decimals = typeof column.dataIndex === 'string' ? byField[column.dataIndex] : undefined;
    if (!format || format.decimalScale !== undefined || decimals === undefined) return column;
    return { ...column, options: { ...column.options, format: { ...format, decimalScale: decimals } } };
  });
};

/** The form schema with each numeric field's precision taken from the column on the same field, unless it sets its own. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withColumnDecimals = (schema: FormItem[], ...columnSets: TableColumns<any>[]): FormItem[] => {
  const byField = getDecimalsByField(...columnSets);
  const apply = (item: FormItem): FormItem => {
    const { columns } = item;
    if (Array.isArray(columns)) return { ...item, columns: columns.map(apply) };
    if (typeof columns === 'function') return { ...item, columns: (...args) => columns(...args).map(apply) };
    const decimals = typeof item.dataIndex === 'string' ? byField[item.dataIndex] : undefined;
    const { fieldProps } = item;
    if (!NUMERIC_VALUE_TYPES.has(item.valueType ?? '') || decimals === undefined || typeof fieldProps === 'function') return item;
    if (fieldProps?.precision !== undefined) return item;
    return { ...item, fieldProps: { ...fieldProps, precision: decimals } };
  };
  return schema.map(apply);
};
